import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already-verified'
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const verifyEmail = async () => {
            const token = searchParams.get('token');

            if (!token) {
                setStatus('error');
                setMessage('Invalid verification link. Please check your email for the correct verification link.');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const response = await axios.get(`${API_BASE_URL}/auth/verify-email?token=${token}`);
                
                if (response.data.alreadyVerified) {
                    setStatus('already-verified');
                    setMessage('Your email is already verified. You can log in now.');
                } else {
                    setStatus('success');
                    setMessage('Email verified successfully! You can now log in to your account.');
                }
                
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/auth/login', { replace: true });
                }, 3000);

            } catch (error) {
                console.error('Email verification error:', error);
                setStatus('error');
                setMessage(
                    error.response?.data?.message || 
                    'Verification failed. The link may be expired or invalid.'
                );
            } finally {
                setIsLoading(false);
            }
        };

        verifyEmail();
    }, [searchParams, navigate]);

    const handleResendVerification = async () => {
        const email = prompt('Please enter your email address to resend verification:');
        if (!email) return;

        try {
            setIsLoading(true);
            await axios.post(`${API_BASE_URL}/auth/resend-verification`, { email });
            alert('Verification email sent! Please check your inbox.');
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to send verification email.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="verify-email-container">
                <div className="verify-email-card">
                    <LoadingSpinner />
                    <h2>Verifying your email...</h2>
                    <p>Please wait while we verify your email address.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="verify-email-container">
            <div className="verify-email-card">
                {status === 'success' && (
                    <>
                        <div className="success-icon">✅</div>
                        <h2>Email Verified Successfully!</h2>
                        <p>{message}</p>
                        <p className="redirect-message">You will be redirected to login in 3 seconds...</p>
                        <Link to="/auth/login" className="login-button">
                            Go to Login Now
                        </Link>
                    </>
                )}

                {status === 'already-verified' && (
                    <>
                        <div className="info-icon">ℹ️</div>
                        <h2>Already Verified</h2>
                        <p>{message}</p>
                        <p className="redirect-message">You will be redirected to login in 3 seconds...</p>
                        <Link to="/auth/login" className="login-button">
                            Go to Login Now
                        </Link>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="error-icon">❌</div>
                        <h2>Verification Failed</h2>
                        <p>{message}</p>
                        <div className="error-actions">
                            <button 
                                onClick={handleResendVerification}
                                className="resend-button"
                            >
                                Resend Verification Email
                            </button>
                            <Link to="/auth/register" className="register-link">
                                Back to Registration
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default VerifyEmail;